// src/pages/tournamentDetails/components/FormatCard.js
import React from "react";

export default function FormatCard({
  isAdmin = false,
  isCompleted = false,
  isRegistering = false,
  isRunning = false,

  buyIn = 0,
  totalRebuys = 0,
  totalAddOns = 0,
  rebuysWindowOpen = false,
  addOnsWindowOpen = false,

  rebuysEnabled = false,
  rebuyCutoffLevel = "",
  addOnsEnabled = false,
  addOnLevel = "",
  addOnChips = 0,
  addOnCost = 0,

  levelItems = [],

  rebuysEnabledDraft = false,
  setRebuysEnabledDraft = () => {},
  rebuyCutoffLevelDraft = "",
  setRebuyCutoffLevelDraft = () => {},

  addOnsEnabledDraft = false,
  setAddOnsEnabledDraft = () => {},
  addOnLevelDraft = "",
  setAddOnLevelDraft = () => {},
  addOnChipsDraft = "0",
  setAddOnChipsDraft = () => {},
  addOnCostDraft = "0",
  setAddOnCostDraft = () => {},

  formatDirty = false,
  savingFormatSettings = false,
  onSaveFormatSettings = () => {},
}) {
  const locked = !isAdmin || isCompleted || !isRegistering;
  const safeLevelItems = Array.isArray(levelItems) ? levelItems : [];

  const rebuyCutoffMissing = !!rebuysEnabledDraft && !rebuyCutoffLevelDraft;
  const addOnLevelMissing = !!addOnsEnabledDraft && !addOnLevelDraft;

  const saveDisabled =
    locked || savingFormatSettings || !formatDirty || rebuyCutoffMissing || addOnLevelMissing;

  const money = (value) => {
    const n = Number(value || 0);
    if (!Number.isFinite(n)) return "$0";
    return `$${n.toLocaleString()}`;
  };

  return (
    <div className="bg-zinc-900 p-4 rounded-lg border border-zinc-800 mb-8">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h3 className="text-xl font-semibold text-[#B87333]">Format</h3>

          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border border-zinc-700 bg-zinc-800/70 text-zinc-200">
              {rebuysEnabled ? "↻ Rebuy" : "Freezeout"}
            </span>

            {addOnsEnabled ? (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border border-blue-500/30 bg-blue-500/10 text-blue-200">
                + Add-On
              </span>
            ) : null}

            {rebuysEnabled ? (
              <span className="text-zinc-400 text-sm">
                Rebuy amount:{" "}
                <span className="text-zinc-200 font-semibold">{money(buyIn)}</span>
              </span>
            ) : null}
          </div>

          <div className="mt-2 text-zinc-500 text-xs space-y-1">
            <div>
              Total rebuys so far:{" "}
              <span className="text-zinc-200 font-semibold">{Number(totalRebuys || 0)}</span>
            </div>

            {rebuysEnabled ? (
              <div>
                Rebuy window:{" "}
                {rebuyCutoffLevel ? (
                  <>
                    until end of{" "}
                    <span className="text-zinc-200 font-semibold">Level {rebuyCutoffLevel}</span>
                    {isRunning ? (
                      <>
                        {" "}
                        •{" "}
                        <span className={rebuysWindowOpen ? "text-green-400" : "text-red-400"}>
                          {rebuysWindowOpen ? "OPEN" : "CLOSED"}
                        </span>
                      </>
                    ) : null}
                  </>
                ) : (
                  <span className="text-zinc-400">Cutoff not set</span>
                )}
              </div>
            ) : null}

            {addOnsEnabled ? (
              <>
                <div>
                  Total add-ons:{" "}
                  <span className="text-zinc-200 font-semibold">{Number(totalAddOns || 0)}</span>
                </div>

                <div>
                  Add-on window:{" "}
                  {addOnLevel ? (
                    <>
                      after end of{" "}
                      <span className="text-zinc-200 font-semibold">Level {addOnLevel}</span> •{" "}
                      <span className="text-zinc-200 font-semibold">
                        +{Number(addOnChips || 0).toLocaleString()} chips
                      </span>{" "}
                      for{" "}
                      <span className="text-zinc-200 font-semibold">{money(addOnCost)}</span>
                      {isRunning ? (
                        <>
                          {" "}
                          •{" "}
                          <span className={addOnsWindowOpen ? "text-green-400" : "text-zinc-400"}>
                            {addOnsWindowOpen ? "AVAILABLE" : "NOT YET"}
                          </span>
                        </>
                      ) : null}
                    </>
                  ) : (
                    <span className="text-zinc-400">Not configured</span>
                  )}
                </div>
              </>
            ) : null}
          </div>
        </div>

        {isAdmin ? (
          <button
            type="button"
            onClick={() => {
              if (!saveDisabled) onSaveFormatSettings();
            }}
            disabled={saveDisabled}
            className={`px-4 py-2 rounded font-semibold ${
              saveDisabled
                ? "bg-zinc-700 text-zinc-300 cursor-not-allowed"
                : "bg-green-700 hover:bg-green-800 text-white"
            }`}
          >
            {savingFormatSettings ? "Saving..." : "Save Format Settings"}
          </button>
        ) : null}
      </div>

      {isAdmin ? (
        <div className="border-t border-zinc-800 pt-4 mt-4">
          <div className="grid gap-4 md:grid-cols-3 items-start">
            <div className="rounded-lg p-3 border border-zinc-800 bg-black/20">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-zinc-200 text-sm font-semibold">Rebuys</p>
                  <p className="text-zinc-500 text-xs">Requires a cutoff level</p>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    if (locked) return;
                    const next = !rebuysEnabledDraft;
                    setRebuysEnabledDraft(next);
                    if (!next) setRebuyCutoffLevelDraft("");
                  }}
                  disabled={locked}
                  className={`px-3 py-2 rounded text-sm font-semibold ${
                    locked
                      ? "bg-zinc-800 text-zinc-500 cursor-not-allowed"
                      : rebuysEnabledDraft
                      ? "bg-purple-700 text-white"
                      : "bg-zinc-800 text-zinc-100"
                  }`}
                >
                  {rebuysEnabledDraft ? "Enabled" : "Disabled"}
                </button>
              </div>

              {rebuysEnabledDraft ? (
                <div className="mt-3">
                  <label className="block text-zinc-300 text-sm mb-1">
                    Rebuy Cutoff (end of level)
                  </label>

                  <select
                    className="w-full p-3 rounded border bg-zinc-800 text-zinc-100 border-zinc-700"
                    value={rebuyCutoffLevelDraft}
                    onChange={(e) => setRebuyCutoffLevelDraft(e.target.value)}
                    disabled={locked}
                  >
                    <option value="">Choose cutoff level…</option>
                    {safeLevelItems.map((lvl, idx) => (
                      <option key={`${lvl?.level || idx}`} value={String(lvl?.level || "")}>
                        Level {lvl?.level}
                      </option>
                    ))}
                  </select>

                  {rebuyCutoffMissing ? (
                    <p className="text-purple-300 text-xs mt-1">
                      Select a cutoff level to enable rebuys.
                    </p>
                  ) : (
                    <p className="text-zinc-500 text-xs mt-1">
                      Rebuys allowed until the selected level ends.
                    </p>
                  )}
                </div>
              ) : null}
            </div>

            <div className="rounded-lg p-3 border border-zinc-800 bg-black/20">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-zinc-200 text-sm font-semibold">Add-ons</p>
                  <p className="text-zinc-500 text-xs">One per active player</p>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    if (locked) return;
                    const next = !addOnsEnabledDraft;
                    setAddOnsEnabledDraft(next);
                    if (!next) {
                      setAddOnLevelDraft("");
                      setAddOnChipsDraft("0");
                      setAddOnCostDraft("0");
                    }
                  }}
                  disabled={locked}
                  className={`px-3 py-2 rounded text-sm font-semibold ${
                    locked
                      ? "bg-zinc-800 text-zinc-500 cursor-not-allowed"
                      : addOnsEnabledDraft
                      ? "bg-blue-700 text-white"
                      : "bg-zinc-800 text-zinc-100"
                  }`}
                >
                  {addOnsEnabledDraft ? "Enabled" : "Disabled"}
                </button>
              </div>

              {addOnsEnabledDraft ? (
                <div className="mt-3 grid gap-2">
                  <div>
                    <label className="block text-zinc-300 text-xs mb-1">Add-on Level</label>
                    <select
                      className="w-full p-2 rounded border bg-zinc-900 text-zinc-100 border-zinc-800"
                      value={addOnLevelDraft}
                      onChange={(e) => setAddOnLevelDraft(e.target.value)}
                      disabled={locked}
                    >
                      <option value="">Choose level…</option>
                      {safeLevelItems.map((lvl, idx) => (
                        <option key={`${lvl?.level || idx}`} value={String(lvl?.level || "")}>
                          Level {lvl?.level}
                        </option>
                      ))}
                    </select>

                    {addOnLevelMissing ? (
                      <p className="text-blue-200 text-xs mt-1">
                        Select a level to enable add-ons.
                      </p>
                    ) : null}
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-zinc-400 text-xs mb-1">Chips</label>
                      <input
                        type="number"
                        className="w-full p-2 bg-zinc-900 text-zinc-100 rounded border border-zinc-800"
                        value={addOnChipsDraft}
                        onChange={(e) => setAddOnChipsDraft(e.target.value)}
                        min="0"
                        step="1"
                        disabled={locked}
                      />
                    </div>

                    <div>
                      <label className="block text-zinc-400 text-xs mb-1">Cost (USD)</label>
                      <input
                        type="number"
                        className="w-full p-2 bg-zinc-900 text-zinc-100 rounded border border-zinc-800"
                        value={addOnCostDraft}
                        onChange={(e) => setAddOnCostDraft(e.target.value)}
                        min="0"
                        step="1"
                        disabled={locked}
                      />
                    </div>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="text-zinc-500 text-xs leading-relaxed">
              <div className="font-semibold text-zinc-300 mb-1">Editing Rules</div>
              <ul className="list-disc pl-5 space-y-1">
                <li>
                  Format can only be changed while{" "}
                  <span className="text-zinc-200 font-semibold">Registering</span>.
                </li>
                <li>Rebuys require a cutoff level.</li>
                <li>
                  Changes apply after clicking{" "}
                  <span className="text-zinc-200 font-semibold">Save Format Settings</span>.
                </li>
              </ul>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}